//==========================================================
// Fichier js principal de l'appli
//==========================================================

import Vue from 'vue'
import App from './App.vue'
import store from './store'
import './registerServiceWorker'
import i18n from './i18n'
import _ from 'lodash'

//==========================================================
// config pour vue Router

import router from './router'
/**
 * Cet intercepteur de navigation permet de définir la locale de l'application selon celle passée dans l'url
 */
router.beforeEach((to, from, next) => {
    i18n.locale =  to.params.locale
    next()
})

// récupération de la locale dans l'url
let localeUrl = window.location.pathname.replace(/^\/([^/]*).*$/, '$1');

/**
 * Cet intercepteur de navigation permet de valider les locales passées aux routes.
 * Si une locale non attendue est trouvée la navigation sera redirigé vers une 404.
 */
router.beforeEach((to, from, next) => {
    let validLocales = [
        'fr',
        'en'
    ]; // locales autorisées dans les routes

    if(to.name == 'notFound'){ // on bypass la route notFound (sinon boucle infinie)
        next()
    }
    else if (!validLocales.includes(to.params.locale)) {
        next({name: 'notFound'});
    }
    else { // r.a.s on continue le routing
        next();
    }
})

/**
 * Cet intercepteur de navigation permet de limiter l'accès aux routes selon que l'utilisateur est authentifié ou non.
 */
router.beforeEach((to, from, next) => {
    //  Routes inaccessibles si non authentifié
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // this route requires auth, check if logged in if not, redirect to login page.
        if (!Vue.prototype.$auth.isAuthenticated()) {
            next({
                name: 'login',
                params: {'locale':to.params.locale},
                query: { redirect: to.fullPath }
            })
        } else {
            next()
        }
    } else {
        next() // make sure to always call next()!
    }
    //////////////////////////////////////////////////////////////////

    //  Routes inaccessibles si authentifié (pour login, signup etc.)
    if (to.matched.some(record => record.meta.requiresNotAuth)) {
        // this route requires no auth, check if logged in if not, redirect to timeclock page.
        if (Vue.prototype.$auth.isAuthenticated()) {
            next({
                name:'timeTracking', // route vers laquelle on sera redirigé si on est déjà authenticated
                params: {'organization':JSON.parse(Vue.prototype.$cookie.get('auth')).organization,'locale':to.params.locale} // ici on utilise le slug de l'organization depuis le cookie d'auth car non accessible dans l'url (sinon privilégier le slug dans l'url)

            })
        } else {
            next()
        }
    } else {
        next() // make sure to always call next()!
    }
})


/**
 * Cet intercepteur de navigation permet de limiter l'accès aux routes selon un rôle utilisateur
 * (nécessite donc d'être authentifié)
 */
router.beforeEach((to, from, next) => {

     // Routes accessibles selon role (& authenticated)
    if (to.matched.some(record => record.meta.role)) {
        // this route requires a specific role, if not redirect to 403 forbidden page.
        if (!Vue.prototype.$auth.isAuthenticated()) {
            next({
                path: `${to.params.locale}/login`,
                query: { redirect: to.fullPath }
            })
        }
        else if (!Vue.prototype.$auth.hasRole(to.meta.role)) {
            next({
                path: `/${to.params.locale}/forbidden`,
                query: {redirect: to.name}
            })
        } else {
            next()
        }
    } else {
        next() // make sure to always call next()!
    }
})


//==========================================================
// config pour moment-duration-format
// À IMPORTER AVANT MOMENT ! (SINON CASSE moment.locale() DE MOMENT)
// https://www.npmjs.com/package/moment-duration-format

import momentDurationFormatSetup  from "moment-duration-format";
momentDurationFormatSetup(moment);

// config pour moment
// https://momentjs.com/docs/
import moment from 'moment-timezone';
moment.locale(localeUrl)
Vue.prototype.$moment = moment;


//==========================================================
// config pour Axios

import axios from 'axios';
import InterceptorMixin from './mixins/InterceptorMixin.js';

let apiBaseUrl = process.env.VUE_APP_API_URL;
let axiosInstance = axios.create({
    baseURL: apiBaseUrl,
    timeout: 10000,
    // axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
});

Vue.prototype.$http = axiosInstance;

// Intercepteur pour le refresh token
axiosInstance.interceptors.response.use(
    function (response) { // Do something with response data
        return response;
    },
    function (error) { // Do something with request error
            // Rafraîchissement du token si 401 'Expired JWT Token'...
            if (InterceptorMixin.methods.InterceptExpiredJwt(error.response,Vue.prototype.$auth  ) ) {

            // return
            axiosInstance
                .post('token/refresh',{refresh_token: Vue.prototype.$auth.getRefreshToken()})
                .then(function (response) {
                    // on met à jour les infos dans $auth (le jwt à changé)
                    Vue.prototype.$auth
                        .setToken(response.data.token)
                        .setRefreshToken(response.data.refresh_token)
                        .setOrganization(response.data.organization)
                        .setRoles(response.data.roles)
                        .setUserSlug(response.data.user)
                        .setWorkspaces(response.data.workspaces)
                        .setActiveWorkspace(response.data.workspaces[0]) // @todo à dynamiser lors de ticket #290 (https://gitlab.com/ousmane1/kronocloud/-/issues/290)
                        .setActiveWorkspaceCurrency(response.data.activeWorkspace.currency) // @todo voir si à dynamiser lors de ticket #290 (https://gitlab.com/ousmane1/kronocloud/-/issues/290)


                        .saveCookie();

                    // on loggue le refresh dans sentry
                    // on active sentry selon l'env (permet si besoin de conserver les erreurs en console en dev env si disabled)
                    if (process.env.VUE_APP_SENTRY_ENABLED=='true') {
                        Sentry.configureScope((scope) => {
                            scope.setUser({"id": response.data.user});
                        });
                        Sentry.captureMessage('️❄️Token refreshed️');
                    }

                    // rechargement de la page... à ce jour je choisi de rafraîchir la page car je n'ai pas réussi lors de mon test en dév à exécuter l'action supposée s'effectuer suite à la requête interceptée. (il s'agissait dans mon cas d'afficher une simple alert()
                    router.go();

                })
                .catch(function (error) {
                    // On déconnecte l'utilisateur (actuellement en test pour fixer ticket https://gitlab.com/ousmane1/kronocloud/-/issues/515)
                    if (process.env.VUE_APP_SENTRY_ENABLED=='true') {// on active sentry selon l'env (permet si besoin de conserver les erreurs en console en dev env si disabled)
                        Sentry.captureMessage('☂️ User logged out because fail to refresh token : ' + error);
                    }
                    let localeUrl = window.location.pathname.replace(/^\/([^/]*).*$/, '$1');
                    router.replace({name: 'logout', params: {locale:i18n.locale }})
                });
        }

        // Logout si 401 'Bad credentials' (refresh_token expiré)
        else if (InterceptorMixin.methods.InterceptBadCredentials(error.response,Vue.prototype.$auth))
            {
            // on redirige vers le logout (dés-authentifiera l'user coté frontend et le redirigera vers le login)
            router.replace({name: 'logout', params: {locale:i18n.locale }})

            store.commit('UpdateGlobalNotificationComp', {
                'isVisible': true,'color': 'is-warning','message': i18n.t('VotreSessionAExpirée')
            });
        }

        return Promise.reject(error);
    });


//==========================================================
// config pour vue-cookie
import VueCookie from 'vue-cookie';
Vue.use(VueCookie);

//==========================================================
// config pour l'auth

import auth from './classes/auth.js';

Vue.prototype.$auth = new auth(VueCookie, axiosInstance, Sentry ,store);

//==========================================================
// config pour vue-mq
import VueMq from 'vue-mq'
Vue.use(VueMq, {
    // définition des breakpoints (basé sur ceux de bulma :https://bulma.io/documentation/overview/responsiveness/#breakpoints)
    breakpoints: {
        mobile:768,
        tablet: 769,
        desktop: 1024,
        widescreen:1216,
        fullhd:  Infinity // note: normalement bulma fullhd commence à 1408
    }
})
//==========================================================
// config pour vue-meta
import Meta from 'vue-meta';
Vue.use(Meta);

//==========================================================
// config pour Sentry (voir Sentry.md)
import * as Sentry from '@sentry/browser'

// on active sentry selon l'env (permet si besoin de conserver les erreurs en console en dev env si disabled)
if (process.env.VUE_APP_SENTRY_ENABLED=='true') {
    Sentry.init({
        dsn: process.env.VUE_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV, // récupère l'environnement
        release: "cronocloud-pwa@" + process.env.VUE_APP_VERSION, // définition de la release
        integrations: [new Sentry.Integrations.Vue({Vue})],
        debug:false
    })
}

Vue.prototype.$sentry = Sentry // on définit une propriété globale

//==========================================================
// config pour vee-validate

import VeeValidate from 'vee-validate';
import { Validator } from 'vee-validate';

// chargement des traductions vee-validate
import fr from 'vee-validate/dist/locale/fr.js';
Validator.localize('fr', fr);
// ajouter une nouvelle locale ici

// import des dictionnaires custom
import customDicFr from './validationI18n/customDic.fr.js';
import customDicEn from './validationI18n/customDic.en.js';

Vue.use(VeeValidate, {
    locale: localeUrl
    // locale: window.location.pathname.replace(/^\/([^/]*).*$/, '$1')
    , //récupère la locale dans l'url. NOTE : je n'ai pas réussi à réutiliser la locale dans i18n ou même dans $route.params.locale, car cette partie du code s'exécutait toujours avant.   à voir si posera problème pour les app hybrides
    dictionary : {
        fr :customDicFr,
        en :customDicEn
    } ,
});

//==========================================================
// config pour fontAwesome
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Imports des icônes utilisées

// AppNavbar icônes
import { faBuilding } from '@fortawesome/free-regular-svg-icons'
import {faEyeSlash, faUserCircle, faGift} from '@fortawesome/free-solid-svg-icons'
import { faBars } from '@fortawesome/free-solid-svg-icons'
// AppMenu icônes
import { faStopwatch } from '@fortawesome/free-solid-svg-icons'
import { faUsers } from '@fortawesome/free-solid-svg-icons'
import { faChartBar } from '@fortawesome/free-solid-svg-icons'
import { faCog } from '@fortawesome/free-solid-svg-icons'
import { faPowerOff } from '@fortawesome/free-solid-svg-icons'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { faSun,faMoon } from '@fortawesome/free-solid-svg-icons'

// PwdRequest/PwdReset
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons'

// UserSettings
import { faIdCard } from '@fortawesome/free-regular-svg-icons'
import { faCheck} from '@fortawesome/free-solid-svg-icons'
import { faAt, faUserCog } from '@fortawesome/free-solid-svg-icons'
import { faKey } from '@fortawesome/free-solid-svg-icons'
import { faAdjust, faEuroSign, faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons'

// Timeclock
import {faSignInAlt}  from '@fortawesome/free-solid-svg-icons'
import {faSignOutAlt}  from '@fortawesome/free-solid-svg-icons'
import {faDesktop}  from '@fortawesome/free-solid-svg-icons'
import {faComment}  from '@fortawesome/free-regular-svg-icons'
import {faCalendarAlt} from "@fortawesome/free-regular-svg-icons"
import {faPencilAlt} from "@fortawesome/free-solid-svg-icons"
//
// GlobalNotification
import {faExclamationCircle} from "@fortawesome/free-solid-svg-icons";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {faBan} from "@fortawesome/free-solid-svg-icons";

// TimeTracking
import {faPlayCircle}  from '@fortawesome/free-solid-svg-icons'
import {faStop}  from '@fortawesome/free-solid-svg-icons'
import {faTasks}  from '@fortawesome/free-solid-svg-icons'
import {faSpinner}  from '@fortawesome/free-solid-svg-icons'
import {faPlay}  from '@fortawesome/free-solid-svg-icons'
import {faBookmark}  from '@fortawesome/free-regular-svg-icons'
import {faFolderOpen}  from '@fortawesome/free-regular-svg-icons'

import {faDollarSign}  from '@fortawesome/free-solid-svg-icons'
import {faUserTie}  from '@fortawesome/free-solid-svg-icons'
import {faPlusCircle}  from '@fortawesome/free-solid-svg-icons'

//ProjectView
import {faLock}  from '@fortawesome/free-solid-svg-icons'
import {faLockOpen}  from '@fortawesome/free-solid-svg-icons'
import {faEllipsisV}  from '@fortawesome/free-solid-svg-icons'
import {faTrashAlt}  from '@fortawesome/free-solid-svg-icons'
import {faFolder} from '@fortawesome/free-solid-svg-icons'
import {faBomb} from '@fortawesome/free-solid-svg-icons'
import {faHourglassHalf,faArrowLeft,faTachometerAlt, faPrint} from '@fortawesome/free-solid-svg-icons'
import {faQuestionCircle} from '@fortawesome/free-regular-svg-icons'

//ReportView
import {
    faChevronDown,
    faChevronRight,
    faChevronUp,
    faSearch,
    faCaretRight,
    faCaretLeft,
    faFilter,
    faSave,
    faChartLine,
    faCaretLeftn,
    faChartPie,
} from "@fortawesome/free-solid-svg-icons";

//UsersView
import {faUserSlash,faCrown,faUser,faEnvelope,faIdBadge,faSkullCrossbones, faMedal} from "@fortawesome/free-solid-svg-icons";

// SignupInvit view
import {faEye, faStar} from "@fortawesome/free-solid-svg-icons";

// Subscription view
import {faHeart,faGlobeAmericas, faExternalLinkAlt, } from "@fortawesome/free-solid-svg-icons";
import {faSmileWink} from
        "@fortawesome/free-regular-svg-icons"

// Subscription view (faqSection)
import {faQuestion,faToggleOn,faMobileAlt,faCreditCard, faPercentage, faTree} from "@fortawesome/free-solid-svg-icons";

// Tags view
import {faTag} from "@fortawesome/free-solid-svg-icons";


library.add(
    faBuilding, faUserCircle, faStopwatch, faChartBar, faCog, faPowerOff, faBars, faTimes, faCheckCircle, faIdCard, faCheck, faAt, faKey,
    faSignInAlt, faSignOutAlt,
    faDesktop, faComment, faCalendarAlt,
    faPencilAlt,
    faExclamationCircle,
    faInfoCircle, faBan, faPlayCircle, faStop, faTasks, faSpinner,
    faPlay, faBookmark,
    faFolderOpen,
    faUsers, faDollarSign, faUserTie, faPlusCircle,
    faLock,
    faLockOpen, faEllipsisV,
    faFolder,
    faTrashAlt,
    faBomb,
    faHourglassHalf, faChartLine, faChartPie, faSave, faCaretRight, faCaretLeft, faSearch, faChevronDown
    , faUserSlash, faChevronUp
    , faUser, faEnvelope, faCrown,faIdBadge,faEye,faEyeSlash,faSkullCrossbones,faChevronRight, faAdjust,faSun,faMoon,faUserCog,faGift, faFilter,faEuroSign,faArrowLeft,faQuestionCircle,faTachometerAlt, faPrint, faMedal, faHeart,faGlobeAmericas,faExternalLinkAlt,
    faQuestion,faToggleOn,faMobileAlt,faCreditCard, faPercentage, faTree, faSmileWink,faLongArrowAltRight,faStar,faTag
)

Vue.component('font-awesome-icon', FontAwesomeIcon)

//==========================================================
// config vue-gtag
// https://matteo-gabriele.gitbook.io/vue-gtag/
// voir Analytics.md

import VueGtag from 'vue-gtag'

Vue.use(VueGtag, {
    config: {
        id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID,
        // debug_mode:true
    },
    includes: [ // pour charger d'autre tag
        { id: 'AW-10781054301' },  // suivi de la campagne google ads
    ],
    appName: process.env.VUE_APP_NAME,
    pageTrackerScreenviewEnabled: true,
    // appVersion: process.env.VUE_APP_VERSION, // ne fonctionne pas à priori

    disableScriptLoad:  process.env.NODE_ENV != 'production' , // désactivation du tracking pour tous env sauf prod


},router);
//==========================================================

// config pour mixpanel-js
// https://www.npmjs.com/package/mixpanel-browser

var mixpanel = require('mixpanel-browser');

mixpanel.init(
    process.env.VUE_APP_MIXPANEL_TOKEN,
    {opt_out_tracking_by_default: process.env.NODE_ENV != 'production'} // désactivation du tracking pour tous env sauf prod
);

Vue.prototype.$mixpanel = mixpanel

//==========================================================
// config pour vue-scrollto
// https://www.npmjs.com/package/vue-scrollto

import VueScrollTo from 'vue-scrollto'
Vue.use(VueScrollTo)
Vue.prototype.$vueScrollTo = VueScrollTo

// // You can also pass in the default options
Vue.use(VueScrollTo, {
    container: "body",
    // duration: 300,
    easing: "ease",
    offset: 0,
    force: true,
    cancelable: true,
    onStart: false,
    onDone: false,
    onCancel: false,
    x: false,
    y: true
})

//==========================================================
// config pour qs
// https://www.npmjs.com/package/qs

import qs from 'qs'
Vue.prototype.$qs = qs;

// ==========================================================
// config pour vue-flatpickr
// https://github.com/ankurk91/vue-flatpickr-component
import VueFlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {French} from 'flatpickr/dist/l10n/fr.js'
import {english} from 'flatpickr/dist/l10n/default.js'

Vue.use(VueFlatPickr);

// Chargement des traductions du datepicker en fonction de la localeUrl
switch (localeUrl) {
    case  'fr':  Vue.prototype.$datepickerI18nConf = {
        locale: French,
        altFormat: 'd/m/Y H:i', // le format de date affiché dans l'input (localisé) https://flatpickr.js.org/formatting/
        time_24hr:true
    }
        ; break;
    case  'en':  Vue.prototype.$datepickerI18nConf = {
        locale: english,
        altFormat: 'm/d/Y h:i K',
        time_24hr:false
    };break;


    default:
        // par défaut on charge le format français @todo à changer après release version en
     Vue.prototype.$datepickerI18nConf = {
        locale: French,
        altFormat: 'd/m/Y H:i', // le format de date affiché dans l'input (localisé) https://flatpickr.js.org/formatting/
        time_24hr:true
    }
    break;

}

//==========================================================
// config pour jspdf
//https://rawgit.com/MrRio/jsPDF/master/docs/index.html
import jsPDF from 'jspdf'
Vue.prototype.$jsPDF = jsPDF;

// config pour dom-to-image
// https://www.npmjs.com/package/dom-to-image
import domtoimage from "dom-to-image";
Vue.prototype.$domtoimage = domtoimage;

//==========================================================
// config pour vue-tippy
// https://www.npmjs.com/package/vue-tippy
import VueTippy, { TippyComponent } from "vue-tippy";
// import "tippy.js/themes/light.css";
import "tippy.js/themes/light.css";


Vue.use(VueTippy, {
    directive: "tippy", // => v-tippy
    flipDuration: 0,

    popperOptions: {
        modifiers: {
            preventOverflow: {
                enabled: false
            },
            hide: {enabled: false}, //@vite un warning https://github.com/atomiks/tippyjs/issues/105 ?

        }
    },
    // ma configuration par défaut ci-dessous, peut-être modifié dans le html
    placement : 'top',
    animation:'shift-away',
    arrow: true,  // affiche un flèche à la tootlip
    interactive : true, // permet de pouvoir survoler la tooltip sans qu'elle se masque
    allowHTML: true,

});
Vue.component("tippy", TippyComponent);

//==========================================================
// config pour firebase performance
// https://firebase.google.com/docs/perf-mon/get-started-web?authuser=0#using-module-bundler
import firebase from "firebase/app";
import "firebase/performance";

if (process.env.NODE_ENV=='production') {
    const firebaseConfig = {
        apiKey: "AIzaSyBnpa0sGZT6rsT-BF2bhSpHV80wJbPgy0M",
        authDomain: "kronocloud-pwa.firebaseapp.com",
        databaseURL: "https://kronocloud-pwa.firebaseio.com",
        projectId: "kronocloud-pwa",
        storageBucket: "kronocloud-pwa.appspot.com",
        messagingSenderId: "139204450309",
        appId: "1:139204450309:web:0ee4863ffa215b211ce46c"
    };

// Initialize Firebase
    firebase.initializeApp(firebaseConfig);

// Initialize Performance Monitoring and get a reference to the service
    /* eslint-disable no-unused-vars */const perf = firebase.performance();/* eslint-disable no-unused-vars */
}
//==========================================================

Vue.config.productionTip = false

new Vue({
  router,
  store,
  i18n,
  _,
  render: h => h(App)
}).$mount('#app')
